import React, { useEffect, useState, useCallback, useMemo } from "react";
import DataTable, { TableColumn, ConditionalStyles } from "react-data-table-component";
import { deleteMail, editMail, getAllMail, saveMail } from "../api/appliApi";
import { getAllGroup } from "../api/appliApi"; // <-- ajouté pour récupérer la liste des groupes
import FilterComponent from "../components/FilterComponent";
import CustomLoader from "../components/LoaderComponent";
import { customHeader, paginationOptions } from "../utils/StyledDataTable";
import { Form } from "react-bootstrap";
import { FormModal } from "../components/FormModal";
import { MdDeleteForever, MdEdit } from "react-icons/md";
import { ConfirmModal } from "../components/ConfirmModal";
import { ToastContainer, toast } from "react-toastify";
import { TableContainer } from "../utils/StyledComponents";
import "../asset/css/emailInput.css";
import plusCircle from "../asset/img/icon/plus-circle.svg";
import { MaillingInterface, UserGroupInterface } from "../utils/Type";
import { EmailInput } from "../components/EmailInput";

const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
};

const clearFormData = () => ({
    id: "",
    groupe: "",
    client: "",
    mail: ""
});

const Mailling: React.FC = () => {
    const [datas, setDatas] = useState<MaillingInterface[]>([]);
    const [groupNameData, setGroupNameData] = useState<UserGroupInterface[]>([]);
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [pending, setPending] = useState(true);
    const [modalShow, setModalShow] = useState(false);
    const [confirmShow, setConfirmShow] = useState(false);
    const [formData, setFormData] = useState(clearFormData());
    const [selectedName, setSelectedName] = useState('');
    const [idToDelete, setIdToDelete] = useState('');

    const fetchData = useCallback(async () => {
        setPending(true);
        try {
            const { data } = await getAllMail();
            setDatas(Array.isArray(data) ? data : []);
        } catch {
            setDatas([]);
        } finally {
            setPending(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        fetchGroups();
    }, [fetchData]);

    const fetchGroups = async () => {
        try {
            const resp = await getAllGroup();
            if (Array.isArray(resp?.data)) {
                setGroupNameData(resp.data);
            } else {
                setGroupNameData([]);
            }
        } catch (err) {
            setGroupNameData([]);
        }
    };

    const filteredData = useMemo(() => {
        const txt = filterText.toLowerCase();
        return datas.filter(d =>
            d.id.toString().includes(txt) ||
            d.groupe.toLowerCase().includes(txt) ||
            d.client.toLowerCase().includes(txt) ||
            d.mail.toLowerCase().includes(txt)
        );
    }, [filterText, datas]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.currentTarget;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEmailChange = (val: string) => setFormData(prev => ({ ...prev, mail: val }));
    const handleClear = () => { setFilterText(''); setResetPaginationToggle(!resetPaginationToggle); };
    const handleClearForm = () => { setFormData(clearFormData()); setSelectedName(''); };
    const notify = (msg: string) => toast(msg);
    const notifyError = (msg: string) => toast.error(msg);

    const handleValidate = async () => {
        try {
            if (formData.id) await editMail.put(formData);
            else await saveMail.post(formData);
            await fetchData();
            handleClearForm();
            notify('Enregistré avec succès !');
            setModalShow(false);
        } catch (err: any) {
            notifyError(err.response?.data?.message || 'Erreur lors de l\'enregistrement.');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.groupe.trim()) return notifyError('Veuillez sélectionner un groupe.'); //TODO: permetre de mettre group vide
        if (!formData.mail.trim()) return notifyError('Veuillez saisir au moins un e-mail.');

        const emails = formData.mail.split(';').map(e => e.trim()).filter(Boolean);
        const invalid = emails.filter(e => !validateEmail(e));
        if (invalid.length) return notifyError(`Emails invalides : ${invalid.join(', ')}`);
        await handleValidate();
    };

    const handleEdit = (item: MaillingInterface) => {
        setFormData(item);
        setSelectedName(item.groupe);
        setModalShow(true);
    };
    const handleShowConfirm = (id: string, name: string) => { setIdToDelete(id); setSelectedName(name); setConfirmShow(true); };
    const handleDelete = async () => {
        try {
            await deleteMail(idToDelete);
            await fetchData(); handleClearForm(); notify('Supprimé avec succès !'); setConfirmShow(false);
        } catch (err: any) { notifyError(err.response?.data?.message || 'Erreur lors de la suppression.'); }
    };

    const columns: TableColumn<MaillingInterface>[] = [
        { name: 'Id', selector: row => row.id, sortable: true, width: '7%' },
        { name: 'Groupe', selector: row => row.groupe, sortable: true },
        { name: 'Filtre client', selector: row => row.client, sortable: true },
        {
            name: 'e-mail',
            cell: row => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {row.mail.split(';').map((email, idx) => (
                        <span key={idx} className="email-chip">{email.trim()}</span>
                    ))}
                </div>
            ),
            sortable: true,
            width: '50%'
        },
        {
            name: 'Action',
            cell: row => (
                <>
                    <MdEdit size={24} title="Modifier" onClick={() => handleEdit(row)} style={{ cursor: "pointer", marginRight: 8 }} />
                    <MdDeleteForever size={24} title="Supprimer" onClick={() => handleShowConfirm(row.id, row.groupe)} style={{ cursor: "pointer" }} />
                </>
            ),
            sortable: false
        }
    ];

    const conditionalRowStyles: ConditionalStyles<MaillingInterface>[] = [
        {
            when: row => filteredData.indexOf(row) % 2 !== 0,
            style: { backgroundColor: "#F6F8FD" },
        },
    ];

    return (
        <div className="custom-content">
            <div className="custom-header">
                <span className="custom-title">Gestion des emails</span>
                <button className="btn-add-circle" onClick={() => setModalShow(true)} disabled={pending}>
                    <span className="btn-text">Ajouter</span>
                    <span className="plus-icon-circle"><img src={plusCircle} alt="icone +" /></span>
                </button>
            </div>

            <ToastContainer />

            <FilterComponent filterText={filterText} onFilter={e => setFilterText(e.target.value)} onClear={handleClear} />

            <TableContainer>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    paginationResetDefaultPage={resetPaginationToggle}
                    conditionalRowStyles={conditionalRowStyles}
                    persistTableHead
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 150px)"
                    progressPending={pending}
                    progressComponent={<CustomLoader />}
                    customStyles={customHeader}
                    paginationComponentOptions={paginationOptions}
                    noDataComponent={<p style={{ marginTop: 20 }}>Aucun enregistrement trouvé.</p>}
                />
            </TableContainer>

            <FormModal
                show={modalShow}
                onValidate={() => document.getElementById('formMail')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))}
                onAbort={() => { setModalShow(false); handleClearForm(); }}
                title={`Détails de la config d'envoi${selectedName ? ` "${selectedName}"` : ''}`}
                body={
                    <Form id="formMail" onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Groupe d’envoi des e-mails clients</Form.Label>

                            <Form.Select
                                name="groupe"
                                value={formData.groupe}
                                onChange={handleChange}
                                aria-label="Sélectionner un groupe"
                                autoFocus
                            >
                                <option value="">-- Sélectionnez un groupe --</option>
                                {groupNameData.map(g => (
                                    <option key={String((g as any).id)} value={g.name}>
                                        {g.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                *Ce groupe est indépendant du <strong>Stockage RI</strong>, et reste le groupe du technicien.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Condition d’envoi selon le nom du client</Form.Label>
                            <Form.Control name="client" value={formData.client} onChange={handleChange} placeholder="Client" />
                        </Form.Group>
                        <div className="mb-3 p-2 rounded border bg-light">
                            <div className="fw-semibold mb-2">Règles client</div>
                            <ul className="mb-0 ps-3">
                                <li>Champ vide : tous les clients</li>
                                <li>TEST* : clients dont le code commence par TEST</li>
                                <li>*TEST : clients dont le code se termine par TEST</li>
                                <li>*TEST* : clients dont le code contient TEST</li>
                                <li>TEST? : clients commençant par TEST suivi d’un seul caractère</li>
                                <li>-TEST* : tous les clients sauf ceux dont le code commence par TEST</li>
                                <li>TEST*; AZER* : clients dont le code commence par TEST ou par AZER</li>
                            </ul>
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Label>Adresse e-mail d’envoi</Form.Label>
                            <EmailInput value={formData.mail} onChange={handleEmailChange} />
                        </Form.Group>
                    </Form>
                }
            />

            <ConfirmModal
                show={confirmShow}
                onValidate={handleDelete}
                onAbort={() => setConfirmShow(false)}
                title="Supprimer le groupe d'envoi"
                body={`Êtes-vous sûr de vouloir supprimer "${selectedName}" ?`}
            />
        </div>
    );
};

export default Mailling;
